import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Linkedin, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const team = [
  {
    name: 'Srdjan Dimitrijevic',
    role: 'Fondateur',
    color: '#f59e0b',
    photo: 'https://customer-assets.emergentagent.com/job_da6066e9-f451-44bf-b6da-787b09c3f2e5/artifacts/etvbbzts_Srdjan-DIMITRIJEVIC.PNG',
    quote: "L'information doit travailler pour l'entreprise, pas l'inverse.",
    formation: "SUP de V' (1999)",
    parcours: [
      { period: '2002-2009', role: 'Fondateur, YUBECOM', desc: 'Management commercial et pilotage d\'équipes de développement France/Serbie' },
      { period: '2 ans', role: 'Consultant SAP', desc: 'Consulting ERP grands comptes' },
      { period: '2009-2026', role: 'Fondateur, Extensions VIP Paris', desc: 'Franchise dans le domaine des extensions capillaires — 16 ans' },
      { period: '2016-auj.', role: 'Fondateur, MKB Cercueils', desc: 'Fabrication et distribution' },
      { period: '2024-auj.', role: 'Fondateur, SANI ADAPT\'', desc: 'Adaptation de salles de bains pour seniors et PMR' },
      { period: '2026', role: 'Fondateur, CLONEIA', desc: '' },
    ],
    summary: 'Entrepreneur série depuis plus de 20 ans. 5 entreprises fondées dans des secteurs très différents. C\'est cette expérience terrain — équipes, clients, fournisseurs — qui lui a fait comprendre le problème de l\'information perdue en entreprise. Cloneia est né de frustrations réelles, pas d\'un labo.',
    featured: true,
  },
  {
    name: 'Srdjan Tijanic',
    role: 'Responsable Architecture & Sécurité',
    color: '#3b82f6',
    photo: 'https://customer-assets.emergentagent.com/job_da6066e9-f451-44bf-b6da-787b09c3f2e5/artifacts/w1f28ffx_Srdjan-TIJANIC.jpg',
    quote: "La sécurité des données n'est pas une option. C'est la fondation.",
    formation: "Université Paris 8 (IT), CNAM (B.Sc. IT), ESSEC Business School (Master Operational Management)",
    parcours: [
      { period: '1998-2009', role: 'Architecte / Chef de projet, Sopra Steria', desc: '10 ans — clients grands comptes : Orange, SFR, GDF, Renault, JCDecaux, Canal+' },
      { period: '2009-2011', role: 'Chef de projet international, Société Générale', desc: 'Déploiement du programme IT pour la filiale en Russie' },
      { period: '2011-2014', role: 'Consultant Process Management, BNP Paribas', desc: 'Réingénierie et optimisation des processus IT' },
      { period: '2014-2017', role: 'Senior Business Developer, Technology Partnership', desc: 'Développement commercial, Belgrade' },
      { period: '2017-auj.', role: 'ERP/IT Project Manager, Blizak', desc: 'Implémentations Odoo multi-secteurs (retail, télécom, recyclage, cosmétiques)' },
    ],
    summary: 'Plus de 25 ans dans l\'IT, de l\'architecture technique au management de projets internationaux. Un profil rare — à la fois technique et business. Il a géré des systèmes critiques dans les plus grandes banques et entreprises françaises. Chez Cloneia, c\'est lui qui garantit la sécurité des données clients.',
  },
  {
    name: 'Justine Blary',
    role: 'Directrice Commerciale',
    color: '#ec4899',
    photo: 'https://customer-assets.emergentagent.com/job_da6066e9-f451-44bf-b6da-787b09c3f2e5/artifacts/2shy099i_Justine-BLARY.jpg',
    quote: "Comprendre le besoin du client avant qu'il ne l'exprime.",
    formation: "BTS Management des Unites Commerciales (Sacre Coeur Amiens), Licence Commerce (CNAM), Master 2 Vente et Marketing (CNAM)",
    parcours: [
      { period: '2017-2019', role: 'Commerciale marché professionnel, Le Groupe La Poste', desc: '2 ans de développement B2B' },
      { period: '2019-2021', role: 'Business Developer puis Middle Office, Groupe Henner', desc: 'Assurance et protection sociale' },
      { period: '2021', role: 'Consultante protection sociale PME, Mercer France', desc: 'Prevoyance et conseil aux PME' },
      { period: '2022-2025', role: 'Chargee de comptes, Innovie', desc: '3 ans et demi de gestion de portefeuille clients' },
    ],
    summary: '7 ans dans le développement commercial B2B, construit sur le terrain. Elle a vendu dans des environnements complexes : assurance, protection sociale, services aux entreprises. Chez Cloneia, c\'est elle qui porte la relation client.',
  },
];

const TeamMemberCard = ({ member, index }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className={`relative group ${member.featured ? 'md:col-span-2' : ''}`}
      data-testid={`team-member-${index}`}
    >
      <div className="relative rounded-2xl border border-white/5 bg-[#0f0f17] overflow-hidden hover:border-white/10 transition-all duration-500">
        {/* Glow */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
          style={{ background: `radial-gradient(ellipse at top, ${member.color}10, transparent 70%)` }}
        />

        <div className={`relative ${member.featured ? 'md:flex' : ''}`}>
          {/* Photo */}
          <div className={`relative overflow-hidden ${member.featured ? 'md:w-2/5' : ''}`}>
            <div className="aspect-[3/4] md:aspect-auto md:h-full">
              <img
                src={member.photo}
                alt={member.name}
                className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-700"
              />
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f17] via-transparent to-transparent" />
            </div>
          </div>

          {/* Content */}
          <div className={`relative p-6 md:p-8 ${member.featured ? 'md:w-3/5 md:py-10' : ''}`}>
            {/* Role badge */}
            <span
              className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4"
              style={{ background: `${member.color}15`, color: member.color, border: `1px solid ${member.color}30` }}
            >
              {member.role}
            </span>

            <h2 className="text-2xl md:text-3xl font-bold text-[#f5f5f5] mb-3 tracking-tight">
              {member.name}
            </h2>

            {/* Quote */}
            <p className="text-base italic mb-4" style={{ color: `${member.color}cc` }}>
              "{member.quote}"
            </p>

            {/* Formation */}
            <p className="text-[#71717a] text-sm mb-4">
              <span className="text-[#a1a1aa] font-medium">Formation : </span>{member.formation}
            </p>

            {/* Summary */}
            <p className="text-[#a1a1aa] text-sm leading-relaxed mb-4">
              {member.summary}
            </p>

            {/* Parcours toggle */}
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-2 text-sm font-medium transition-colors hover:opacity-80"
              style={{ color: member.color }}
              data-testid={`team-parcours-toggle-${index}`}
            >
              <ArrowRight className={`w-3.5 h-3.5 transition-transform duration-300 ${expanded ? 'rotate-90' : ''}`} />
              {expanded ? 'Masquer le parcours' : 'Voir le parcours complet'}
            </button>

            {/* Expanded parcours */}
            <motion.div
              initial={false}
              animate={{ height: expanded ? 'auto' : 0, opacity: expanded ? 1 : 0 }}
              transition={{ duration: 0.4 }}
              className="overflow-hidden"
            >
              <div className="pt-4 space-y-3">
                {member.parcours.map((p, i) => (
                  <div key={i} className="flex gap-3 text-sm">
                    <span className="text-[#52525b] font-mono text-xs mt-0.5 shrink-0 w-24">{p.period}</span>
                    <div>
                      <p className="text-[#f5f5f5] font-medium">{p.role}</p>
                      {p.desc && <p className="text-[#71717a] text-xs mt-0.5">{p.desc}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const TeamPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#09090b]">
      <Helmet>
        <title>L'équipe dirigeante | Cloneia</title>
        <meta name="description" content="Découvrez l'équipe derrière Cloneia. Entrepreneurs, architectes IT et experts commerciaux au service de la mémoire d'entreprise." />
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 md:px-12">
        <div className="max-w-5xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm uppercase tracking-[0.2em] text-[#3b82f6] font-bold mb-4"
          >
            Équipe de direction
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#f5f5f5] tracking-tight mb-6"
          >
            Les visages derrière le clone.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-[#a1a1aa] text-base md:text-lg max-w-2xl mx-auto"
          >
            Une équipe qui croit que l'information d'entreprise mérite mieux que des dossiers partagés et des mails perdus.
          </motion.p>
        </div>
      </section>

      {/* Team Grid */}
      <section className="pb-24 px-6 md:px-12">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          {team.map((member, i) => (
            <TeamMemberCard key={member.name} member={member} index={i} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-10 rounded-2xl border border-white/5 bg-[#0f0f17]"
          >
            <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-3">
              Une question ? Un projet ?
            </h2>
            <p className="text-[#71717a] mb-6">
              Que vous soyez investisseur, journaliste ou futur client, nous sommes là.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#3b82f6] hover:bg-[#2563eb] text-white font-medium transition-colors"
                data-testid="team-contact-cta"
              >
                Nous contacter
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-white/10 hover:border-white/20 text-[#f5f5f5] font-medium transition-colors"
                onClick={() => setTimeout(() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' }), 100)}
                data-testid="team-demo-cta"
              >
                Demander une demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TeamPage;
